import { useContext } from 'react';
import { toast } from 'react-toastify';
import { AxiosError } from 'axios';
import { importParcelsStatus, importParcelsResult } from 'services/API/Cooperative';
import { displayErrorMessage, displayMessage } from 'utils/tools_functions';
import { LoadingContext } from 'utils/context';
import { ImportContext } from 'providers/ImportStatus';
import { IMPORT_TYPE } from 'utils/GlobalVariables';
import { STATUS_CODES } from './importStatus.constants';

const LOADING_AND_ERROR_MESSAGES = {
  [IMPORT_TYPE.telepac]: {
    loadingMessage: 'IMPORT_TELEPAC_LOADING',
    errorMessage: 'ERR_IMPORT_TELEPAC',
  },
  [IMPORT_TYPE.apia]: {
    loadingMessage: 'IMPORT_APIA_LOADING',
    errorMessage: 'ERR_IMPORT_APIA',
  },
  [IMPORT_TYPE.wiuz]: {
    loadingMessage: 'IMPORT_WIUZ_LOADING',
    errorMessage: 'ERR_IMPORT_WIUZ',
  },
  [IMPORT_TYPE.csv]: {
    loadingMessage: 'IMPORT_CSV_LOADING',
    errorMessage: 'ERR_IMPORT_CSV',
  },
  [IMPORT_TYPE.geofolia]: {
    loadingMessage: 'IMPORT_GEOFOLIA_LOADING',
    errorMessage: 'ERR_IMPORT_GEOFOLIA',
  },
};

export const useCheckStatus = () => {
  const { setCustomText, setLoading } = useContext(LoadingContext);
  const { importType, geofoliaImportSteps, setResult, setGeofoliaImportSteps } =
    useContext(ImportContext);

  const getJobResult = async (jobId, message) => {
    try {
      const response = await importParcelsResult(jobId);
      setResult(response.data, jobId);

      if (message === STATUS_CODES.FAILED) {
        return;
      }

      // Geofolia : gestion des étapes
      if (importType === IMPORT_TYPE.geofolia) {
        if (geofoliaImportSteps === 0) {
          setGeofoliaImportSteps(1);
        } else if (geofoliaImportSteps === 1) {
          setGeofoliaImportSteps(2);
        }
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        toast.error(
          error?.response?.data?.message ??
            displayErrorMessage(
              LOADING_AND_ERROR_MESSAGES[importType]?.errorMessage || 'ERR_IMPORT'
            )
        );
      } else {
        toast.error(displayErrorMessage('ERR_UNEXPECTED'));
      }
    }
  };

  const checkStatus = (jobId) => {
    setLoading(true);
    setCustomText(displayMessage(LOADING_AND_ERROR_MESSAGES[importType]?.loadingMessage));

    const POLLING_INTERVAL = 3000;

    const poll = async () => {
      const startTime = Date.now();

      try {
        const response = await importParcelsStatus(jobId);
        const { message: statusMessage, statusCode } = response.data;

        if (statusCode !== STATUS_CODES.IN_PROGRESS) {
          await getJobResult(jobId, statusMessage);
        } else {
          const elapsedTime = Date.now() - startTime;
          const remainingTime = Math.max(0, POLLING_INTERVAL - elapsedTime);

          // poll will run immediately if remaining time is 0, this mean API response took more than POLLING_INTERVAL
          // otherwise, it will run after remaining time
          setTimeout(poll, remainingTime);
        }
      } catch (error) {
        if (error instanceof AxiosError) {
          toast.error(error?.response?.data?.message ?? displayErrorMessage('ERR_IMPORT'));
        } else {
          toast.error(displayErrorMessage('ERR_UNEXPECTED'));
        }
      }
    };

    poll();
  };

  return checkStatus;
};
